import React, {useState} from "react";
import {Alert, Collapse, Container, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import {PiCaretDown, PiCaretRight, PiCircleFill} from "react-icons/pi";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useNutzungLive from "../../Components/Nutzung/useNutzungLive.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {formatNumber} from "../../Components/Statistics/format.js";
import {bereichFarbe, formatDateTimeDE, formatDuration, formatTimeDE} from "../../Components/Nutzung/format.js";
import {seitenName} from "../../Components/Nutzung/routeLabels.js";
import {BereichLink} from "../../Components/Nutzung/Links.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";
import NutzungProfilKennzahlen from "../../Components/Nutzung/NutzungProfilKennzahlen.jsx";
import NutzungProfilSitzungen from "../../Components/Nutzung/NutzungProfilSitzungen.jsx";
import NutzungProfilBereichsring from "../../Components/Nutzung/NutzungProfilBereichsring.jsx";
import NutzungProfilMuster from "../../Components/Nutzung/NutzungProfilMuster.jsx";
import NutzungProfilGeraete from "../../Components/Nutzung/NutzungProfilGeraete.jsx";
import NutzungAenderungDetails from "../../Components/Nutzung/NutzungAenderungDetails.jsx";

/** Kürzel für den Avatar-Kreis - erster Buchstabe von Vor- und Nachname,
 * bei nur einem Namensteil dessen erste zwei Buchstaben. */
function kuerzel(name) {
    const teile = (name || "").trim().split(/\s+/).filter(Boolean);
    if (teile.length === 0) {
        return "?";
    }
    if (teile.length === 1) {
        return teile[0].slice(0, 2).toUpperCase();
    }
    return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
}

/**
 * Kopfkarte: Kürzel-Kreis, Name, Kontext (Abteilung/Anmeldung/Sitzungen)
 * und rechts - nur wenn die Person gerade wirklich aktiv ist - der Kasten
 * "Jetzt aktiv". "Jetzt" kommt bevorzugt aus der aktuellen Wegverlauf-
 * Station (ist_aktuell, liefert eine exakte Dauer), ersatzweise aus der
 * Live-Liste (/nutzung/daten/live), falls der Wegverlauf noch nicht
 * antwortet - siehe Auftrag Teil 3.
 */
function Kopfkarte({mitarbeiter, kennzahlen, aktuelleStation, liveEintrag}) {
    const istJetztAktiv = Boolean(aktuelleStation) || Boolean(liveEintrag?.aktiv);
    const jetztSeite = aktuelleStation
        ? seitenName(aktuelleStation.route || aktuelleStation.seite)
        : liveEintrag
        ? seitenName(liveEintrag.seite)
        : null;
    const jetztDauer = aktuelleStation?.dauer_sekunden != null ? formatDuration(aktuelleStation.dauer_sekunden) : null;

    const angemeldetSeitLabel = liveEintrag?.seit
        ? `angemeldet seit ${formatTimeDE(liveEintrag.seit)} Uhr`
        : kennzahlen?.letzte_anmeldung
        ? `letzte Anmeldung ${formatDateTimeDE(kennzahlen.letzte_anmeldung)}`
        : null;

    const kopfTeile = [];
    if (mitarbeiter?.abteilung) {
        kopfTeile.push(mitarbeiter.abteilung);
    }
    if (angemeldetSeitLabel) {
        kopfTeile.push(angemeldetSeitLabel);
    }
    if (kennzahlen?.sitzungen != null) {
        kopfTeile.push(`${formatNumber(kennzahlen.sitzungen)} ${kennzahlen.sitzungen === 1 ? "Sitzung" : "Sitzungen"} im Zeitraum`);
    }

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-body d-flex align-items-center flex-wrap gap-3">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
                    style={{width: 52, height: 52, fontSize: "1.05rem", background: "linear-gradient(135deg, var(--bs-primary), #5c8f00)"}}
                    aria-hidden="true"
                >
                    {kuerzel(mitarbeiter?.name)}
                </div>
                <div>
                    <div className="fs-5 fw-semibold">{mitarbeiter?.name || "Unbekannt"}</div>
                    {kopfTeile.length > 0 && <div className="small text-muted">{kopfTeile.join(" · ")}</div>}
                </div>

                {istJetztAktiv && jetztSeite && (
                    <div
                        className="ms-lg-auto rounded-3 px-3 py-2"
                        style={{background: "#f4faf6", border: "1px solid #bcd9c4", minWidth: 220}}
                    >
                        <div className="d-flex align-items-center gap-2 small fw-semibold" style={{color: "#2e7d4f"}}>
                            <PiCircleFill className="nutzung-puls" size={10} aria-hidden="true" />
                            Jetzt aktiv
                        </div>
                        <div className="small mt-1">
                            {jetztSeite}
                            {jetztDauer && <> · seit {jetztDauer}</>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Eine Station des Wegverlaufs - Zeitspanne, Seite, Bereichsmarke, Dauer
 * und aufklappbare Einzelschritte (taetigkeiten). Die aktuelle Station
 * bekommt einen grünen, pulsierenden Punkt statt der sonst violetten
 * Zeitleisten-Markierung. */
function WegverlaufStation({station, istLetzte}) {
    const [offen, setOffen] = useState(false);
    const schritte = Array.isArray(station.taetigkeiten) ? station.taetigkeiten : [];
    const zeittext = station.ist_aktuell
        ? `${formatTimeDE(station.von)} – jetzt`
        : `${formatTimeDE(station.von)} – ${formatTimeDE(station.bis)} Uhr`;
    const dauerText = station.aktiv_sekunden
        ? `${formatDuration(station.dauer_sekunden)} · davon ${formatDuration(station.aktiv_sekunden)} aktiv`
        : formatDuration(station.dauer_sekunden);
    const punktfarbe = station.ist_aktuell ? "#2e8b57" : "#c3a9ee";

    return (
        <div
            className="position-relative ps-4"
            style={{
                borderLeft: istLetzte ? "2px solid transparent" : "2px solid #e4e8ed",
                marginLeft: 4,
                paddingBottom: istLetzte ? 0 : "1rem",
            }}
        >
            <span
                className={`position-absolute rounded-circle ${station.ist_aktuell ? "nutzung-puls" : ""}`}
                style={{
                    left: -6,
                    top: 5,
                    width: 10,
                    height: 10,
                    background: punktfarbe,
                    border: "2px solid #fff",
                    boxShadow: `0 0 0 1px ${punktfarbe}`,
                }}
                aria-hidden="true"
            />
            <div className="small text-muted" style={{fontVariantNumeric: "tabular-nums"}}>{zeittext}</div>
            <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                <span className="fw-semibold">{seitenName(station.route || station.seite)}</span>
                <span
                    className="badge fw-normal"
                    style={{backgroundColor: `${bereichFarbe(station.bereich)}22`, color: bereichFarbe(station.bereich)}}
                >
                    <BereichLink bereich={station.bereich} className="text-decoration-none" style={{color: "inherit"}} />
                </span>
            </div>
            <div className="small text-muted mt-1">{dauerText}</div>

            {schritte.length > 0 && (
                <div className="mt-1">
                    <button
                        type="button"
                        className="btn btn-link btn-sm text-decoration-none px-0 d-inline-flex align-items-center gap-1"
                        onClick={() => setOffen((v) => !v)}
                        aria-expanded={offen}
                    >
                        {offen ? <PiCaretDown aria-hidden="true" /> : <PiCaretRight aria-hidden="true" />}
                        {schritte.length} {schritte.length === 1 ? "Schritt" : "Schritte"}
                    </button>
                    <Collapse in={offen}>
                        <ul className="list-unstyled small mb-0 mt-1" style={{maxWidth: 480}}>
                            {schritte.map((schritt, index) => (
                                <li key={index} className="d-flex gap-2 py-1 border-bottom">
                                    <span className="text-muted flex-shrink-0" style={{minWidth: 46, fontVariantNumeric: "tabular-nums"}}>
                                        {formatTimeDE(schritt.zeit)}
                                    </span>
                                    <span>{schritt.bezeichnung || schritt.art || "–"}</span>
                                </li>
                            ))}
                        </ul>
                    </Collapse>
                </div>
            )}
        </div>
    );
}

/** Wegverlauf als senkrechte Zeitleiste, neueste Station oben (der
 * Endpunkt liefert bereits chronologisch absteigend, siehe Spezifikation
 * Abschnitt 3). */
function Wegverlauf({stationen, loading, error}) {
    const rows = Array.isArray(stationen) ? stationen : [];
    return (
        <NutzungWidget
            title="Wegverlauf"
            help="Jede Station ist ein zusammenhängender Aufenthalt auf einer Seite - unterbrochen bei Seitenwechsel oder mehr als 30 Minuten Lücke. Zum Aufklappen anklicken für die Einzelschritte."
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            height={200}
        >
            <div className="d-flex flex-column">
                {rows.map((station, index) => (
                    <WegverlaufStation key={`${station.von}-${index}`} station={station} istLetzte={index === rows.length - 1} />
                ))}
            </div>
        </NutzungWidget>
    );
}

/** Ein Abschnitt des Tagesbands - farbige Fläche (Bereich) oder graue
 * Lücke (Anwesenheit ohne Handlung), mit Hover-Erklärung über ein eigenes
 * Tooltip statt eines nativen title-Attributs. */
function TagesbandAbschnitt({segment, breite}) {
    const farbe = segment.bereich ? bereichFarbe(segment.bereich) : "#eceef1";
    const beschreibung = segment.bereich
        ? bereichFarbe && segment.bereich
        : null;
    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip>
                    {formatTimeDE(segment.von)}–{formatTimeDE(segment.bis)} Uhr
                    {segment.bereich ? <> · {segment.bereich}</> : " · anwesend, ohne Handlung"}
                </Tooltip>
            }
        >
            <div style={{width: `${breite}%`, background: farbe, height: "100%"}} />
        </OverlayTrigger>
    );
}

function TagesbandInhalt({segmente}) {
    const start = new Date(segmente[0].von).getTime();
    const ende = new Date(segmente[segmente.length - 1].bis).getTime();
    const spanne = Math.max(ende - start, 1);
    const bereicheImBand = [...new Set(segmente.filter((s) => s.bereich).map((s) => s.bereich))];
    const TICKS = 5;
    const ticks = Array.from({length: TICKS}, (_, i) => new Date(start + (spanne * i) / (TICKS - 1)));

    return (
        <div>
            <div className="d-flex rounded-2 overflow-hidden" style={{height: 26, border: "1px solid #e4e8ed"}}>
                {segmente.map((segment, index) => {
                    const von = new Date(segment.von).getTime();
                    const bis = new Date(segment.bis).getTime();
                    const breite = Math.max(((bis - von) / spanne) * 100, 0.3);
                    return <TagesbandAbschnitt key={index} segment={segment} breite={breite} />;
                })}
            </div>
            <div className="d-flex justify-content-between small text-muted mt-1">
                {ticks.map((tick, index) => (
                    <span key={index}>{formatTimeDE(tick.toISOString())}</span>
                ))}
            </div>
            {bereicheImBand.length > 0 && (
                <div className="d-flex flex-wrap align-items-center gap-3 small text-muted mt-2">
                    {bereicheImBand.map((bereich) => (
                        <span key={bereich} className="d-inline-flex align-items-center gap-1">
                            <span
                                aria-hidden="true"
                                style={{width: 9, height: 9, borderRadius: 2, background: bereichFarbe(bereich), display: "inline-block"}}
                            />
                            <BereichLink bereich={bereich} className="text-muted" />
                        </span>
                    ))}
                    <span className="d-inline-flex align-items-center gap-1">
                        <span aria-hidden="true" style={{width: 9, height: 9, borderRadius: 2, background: "#eceef1", display: "inline-block"}} />
                        anwesend, ohne Handlung
                    </span>
                </div>
            )}
        </div>
    );
}

/** Tagesband: lückenloses waagerechtes Band über den betrachteten Tag
 * (Endpunkt /nutzung/daten/tagesband/{user}), darunter eine Zeitachse und
 * die Legende der vorkommenden Bereiche. */
function Tagesband({segmente, loading, error}) {
    const rows = Array.isArray(segmente) ? [...segmente].sort((a, b) => new Date(a.von) - new Date(b.von)) : [];
    return (
        <NutzungWidget
            title="Der Tag im Überblick"
            help="Waagerechtes Band über den gewählten Tag - eingefärbt nach Bereich, graue Abschnitte sind Anwesenheit ohne Handlung."
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            height={110}
        >
            <TagesbandInhalt segmente={rows} />
        </NutzungWidget>
    );
}

/** Änderungen dieser Person - derselbe Endpunkt wie in Pages/Nutzung/
 * Ereignisse.jsx (/nutzung/daten/aenderungen), hier fest auf user_id
 * dieses Mitarbeiters gefiltert und ohne eigene Paginierungsleiste (die
 * letzten 15 reichen im Profilkontext). */
function AenderungenListe({rows, loading, error}) {
    const zeilen = Array.isArray(rows) ? rows : [];
    return (
        <NutzungWidget
            title="Was diese Person geändert hat"
            help="Die jüngsten Datenänderungen im gewählten Zeitraum, neueste zuerst."
            loading={loading}
            error={error}
            empty={!loading && !error && zeilen.length === 0}
            height={160}
        >
            <div style={{overflowX: "auto"}}>
                <Table size="sm" className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th style={{width: 90}}>Zeit</th>
                            <th style={{width: 190}}>Datensatz</th>
                            <th>Änderung</th>
                        </tr>
                    </thead>
                    <tbody>
                        {zeilen.map((zeile) => (
                            <tr key={zeile.id}>
                                <td className="small text-muted" style={{fontVariantNumeric: "tabular-nums"}}>
                                    {formatTimeDE(zeile.zeit)} Uhr
                                </td>
                                <td className="small">
                                    <span className="badge bg-body-secondary text-body border fw-normal">
                                        {zeile.modell}
                                        {zeile.model_id ? ` #${zeile.model_id}` : ""}
                                    </span>
                                    {zeile.bezeichnung && (
                                        <div className="text-muted" style={{fontSize: ".72rem"}}>{zeile.bezeichnung}</div>
                                    )}
                                </td>
                                <td><NutzungAenderungDetails changes={zeile.aenderungen} /></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </NutzungWidget>
    );
}

/**
 * Einzelprofil eines Mitarbeiters - Neubau nach dem freigegebenen Entwurf
 * (nutzung-konzept-neu, Abschnitt "Der Entwurf: Ein Mitarbeiterprofil"):
 * Kopfkarte mit Jetzt-Stand, Wegverlauf als Zeitleiste, Tagesband,
 * Änderungen dieser Person - und weiter unten wie bisher Sitzungen und
 * Geräte (Auftrag Teil 3, Punkt "Geräte und Anmeldungen ... nach unten
 * gerückt").
 *
 * Wegverlauf und Tagesband sind NEUE Endpunkte (Spezifikation Abschnitt 3),
 * die parallel im Backend entstehen - solange sie noch nicht antworten,
 * zeigt NutzungWidget dafür automatisch die Fehlerkachel statt die Seite
 * abstürzen zu lassen.
 */
export default function NutzungProfil() {
    const {mitarbeiter} = usePage().props;
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const live = useNutzungLive();

    // Der Mitarbeiter kommt über die Route (/nutzung/mitarbeiter/{user}),
    // nicht über den Filter - der eigene user_id-Filter wird deshalb
    // ignoriert, statt versehentlich einen zweiten Mitarbeiter zu mischen.
    const profilFilters = {...filters, user_id: undefined};
    const hatMitarbeiter = Boolean(mitarbeiter?.id);

    const profil = useStatisticsData(
        hatMitarbeiter ? `/nutzung/daten/profil/${mitarbeiter.id}` : null,
        profilFilters,
        {skip: !hatMitarbeiter}
    );
    const wegverlauf = useStatisticsData(
        hatMitarbeiter ? `/nutzung/daten/wegverlauf/${mitarbeiter.id}` : null,
        profilFilters,
        {skip: !hatMitarbeiter}
    );
    // Das Tagesband zeigt GENAU EINEN Tag - anders als der frei wählbare
    // Zeitraum der übrigen Auswertung. Verankert auf "bis" (das Ende des
    // gewählten Zeitraums, im Standardfall heute), damit ein Mehrtages-
    // Zeitraum nicht mehrere Tage in ein einziges Band pressen muss.
    const tagesbandTag = filters.bis || filters.von;
    const tagesband = useStatisticsData(
        hatMitarbeiter && tagesbandTag ? `/nutzung/daten/tagesband/${mitarbeiter.id}` : null,
        {von: tagesbandTag, bis: tagesbandTag},
        {skip: !hatMitarbeiter || !tagesbandTag}
    );
    const aenderungen = useStatisticsData(
        hatMitarbeiter ? "/nutzung/daten/aenderungen" : null,
        {...profilFilters, user_id: mitarbeiter?.id, per_page: 15},
        {skip: !hatMitarbeiter}
    );

    if (!hatMitarbeiter) {
        return (
            <Container fluid className="pt-2 pb-4">
                <Alert variant="danger">Dieser Mitarbeiter konnte nicht ermittelt werden.</Alert>
            </Container>
        );
    }

    const wegverlaufRows = Array.isArray(wegverlauf.data) ? wegverlauf.data : [];
    const aktuelleStation = wegverlaufRows.find((s) => s.ist_aktuell);
    const liveEintrag = live.rows.find((r) => String(r.user_id) === String(mitarbeiter.id));

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout
                gruppe="Mitarbeiter"
                titel={mitarbeiter.name || "Mitarbeiterprofil"}
                breadcrumbLabel={mitarbeiter.name}
                nutzungFilters={nutzungFilters}
                showMitarbeiter={false}
                showBereich={false}
                aktuellesProfil={{id: mitarbeiter.id, name: mitarbeiter.name}}
            >
                <div className="d-flex flex-column gap-3">
                    <Kopfkarte
                        mitarbeiter={mitarbeiter}
                        kennzahlen={profil.data?.kennzahlen}
                        aktuelleStation={aktuelleStation}
                        liveEintrag={liveEintrag}
                    />

                    <NutzungProfilKennzahlen data={profil.data?.kennzahlen} loading={profil.loading} error={profil.error} />

                    <Wegverlauf stationen={wegverlauf.data} loading={wegverlauf.loading} error={wegverlauf.error} />

                    <Tagesband segmente={tagesband.data} loading={tagesband.loading} error={tagesband.error} />

                    <div className="row g-3">
                        <div className="col-lg-5">
                            <NutzungProfilBereichsring bereiche={profil.data?.bereiche} loading={profil.loading} error={profil.error} />
                        </div>
                        <div className="col-lg-7">
                            <NutzungProfilMuster muster={profil.data?.muster} loading={profil.loading} error={profil.error} />
                        </div>
                    </div>

                    <AenderungenListe rows={aenderungen.data?.rows} loading={aenderungen.loading} error={aenderungen.error} />

                    {/* Wie bisher, nur nach unten gerückt (Auftrag Teil 3). */}
                    <div className="row g-3">
                        <div className="col-lg-7">
                            <NutzungProfilSitzungen sitzungen={profil.data?.sitzungen} loading={profil.loading} error={profil.error} />
                        </div>
                        <div className="col-lg-5">
                            <NutzungProfilGeraete geraete={profil.data?.geraete} loading={profil.loading} error={profil.error} />
                        </div>
                    </div>
                </div>
            </NutzungLayout>
        </Container>
    );
}
